import { useCallback, useEffect, useMemo, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import { useAppContextNonNull } from "@contexts/AppContext";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwBadge from "../../shared/OwBadge/OwBadge";
import InProductionBadge from "../../shared/InProductionBadge/InProductionBadge";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IGetReleasableStateDTO from "../../../models/DTO/IGetReleasableStateDTO";
import IOnGetReleasableStateDTO, { IReleasableLine } from "../../../models/DTO/IOnGetReleasableStateDTO";
import IFinalizeShipmentReleaseDTO, { IFinalizeShipmentReleaseLine } from "../../../models/DTO/IFinalizeShipmentReleaseDTO";
import IOnFinalizeShipmentReleaseDTO from "../../../models/DTO/IOnFinalizeShipmentReleaseDTO";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import OrderStatesEnum from "../../../models/OrderStatesEnum";
import "./ShipmentReleaseView.scss";

/**
 * Release for Shipment screen (packing-list-style): the explicit commit that
 * walks an order's outstanding quantities down. Non-serialized lines take a
 * qty (default: full outstanding); serialized lines commit selected eligible
 * serials (default: all selected). A confirmation step summarizes the release
 * before finalizing.
 */
export const ShipmentReleaseView = () => {
    const { supplierModel, userModel, uiContext, socket } = useAppContextNonNull();
    const navigate = useNavigate();

    const match = useMatch("/orders/:order_guid/release");
    const orderGuid = match?.params.order_guid || "";

    const crumbs: IBreadcrumbs[] = useMemo(() => {
        return [
            { title: "Orders", url: "/orders" },
            { title: "Release for Shipment", url: `/orders/${orderGuid}/release` },
        ];
    }, [orderGuid]);

    const [releasableState, setReleasableState] = useState<IOnGetReleasableStateDTO | null>(null);
    const [qtyByItemId, setQtyByItemId] = useState<{ [orderItemId: number]: number }>({});
    const [selectedSerialsByItemId, setSelectedSerialsByItemId] = useState<{ [orderItemId: number]: number[] }>({});
    const [isConfirming, setIsConfirming] = useState(false);
    const [isFinalizeActive, setIsFinalizeActive] = useState(false);

    const notify = (level: NotificationLevelEnum, message: string, skipTranslate = false) => {
        const notification: INotification = {
            level,
            position: NotificationPositionEnum.tc,
            message,
            autoDismiss: 6,
            skipMessageTranslate: skipTranslate,
        };

        uiContext.notifyFn(null, notification);
    };

    const getReleasableState = useCallback(() => {
        const payload: IGetReleasableStateDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid,
        };

        socket.emit(EndpointsEnum.OrderGetReleasableState, payload);
    }, [orderGuid, socket, supplierModel.guid, userModel.guid, userModel.token]);

    const onGetReleasableState = useCallback((data: IOnGetReleasableStateDTO) => {
        uiContext.setActivityIndicator(false);

        document.title = "Orderwerks >> Release for Shipment";

        // Defaults: full outstanding qty for non-serialized lines, all eligible
        // serials selected for serialized lines.
        const qtyDefaults: { [orderItemId: number]: number } = {};
        const serialDefaults: { [orderItemId: number]: number[] } = {};

        for (const line of data.lines) {
            if (line.hasSerials) {
                serialDefaults[line.orderItemId] = (line.eligibleSerials || []).map((serial) => serial.id);
            } else {
                qtyDefaults[line.orderItemId] = line.outstanding;
            }
        }

        setQtyByItemId(qtyDefaults);
        setSelectedSerialsByItemId(serialDefaults);
        setReleasableState(data);
    }, []);

    const onFinalizeShipmentRelease = useCallback(
        (data: IOnFinalizeShipmentReleaseDTO) => {
            setIsFinalizeActive(false);

            if (data.error) {
                notify(NotificationLevelEnum.error, data.error.message, true);
                setIsConfirming(false);
                // Re-fetch so the screen reflects current outstanding/eligible state.
                getReleasableState();
                return;
            }

            if (data.fullyReleased) {
                notify(NotificationLevelEnum.success, "order_fully_shipped");
            } else {
                notify(NotificationLevelEnum.success, "release_successful");
            }

            navigate(`/orders/${data.orderGuid}/entry`);
        },
        [getReleasableState, navigate]
    );

    useEffect(() => {
        const sockets: IUiSocketSetup[] = [
            { endpoint: EndpointsEnum.OrderOnGetReleasableState, fn: onGetReleasableState },
            { endpoint: EndpointsEnum.OrderOnFinalizeShipmentRelease, fn: onFinalizeShipmentRelease },
        ];

        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        uiContext.setActivityIndicator(true);
        getReleasableState();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, [onGetReleasableState, onFinalizeShipmentRelease, getReleasableState]);

    const handleQtyChange = (line: IReleasableLine, value: string) => {
        let qty = Number(value);

        if (isNaN(qty) || qty < 0) {
            qty = 0;
        }

        if (qty > line.outstanding) {
            qty = line.outstanding;
        }

        setQtyByItemId((prev) => ({ ...prev, [line.orderItemId]: qty }));
    };

    const toggleSerial = (line: IReleasableLine, serialId: number) => {
        setSelectedSerialsByItemId((prev) => {
            const selected = prev[line.orderItemId] || [];
            const next = selected.includes(serialId) ? selected.filter((id) => id !== serialId) : [...selected, serialId];

            return { ...prev, [line.orderItemId]: next };
        });
    };

    const toggleAllSerials = (line: IReleasableLine) => {
        setSelectedSerialsByItemId((prev) => {
            const eligible = (line.eligibleSerials || []).map((serial) => serial.id);
            const selected = prev[line.orderItemId] || [];

            return { ...prev, [line.orderItemId]: selected.length === eligible.length ? [] : eligible };
        });
    };

    const releaseLines: IFinalizeShipmentReleaseLine[] = useMemo(() => {
        const lines: IFinalizeShipmentReleaseLine[] = [];

        for (const line of releasableState?.lines || []) {
            if (line.hasSerials) {
                const serialIds = selectedSerialsByItemId[line.orderItemId] || [];

                if (serialIds.length > 0) {
                    lines.push({ orderItemId: line.orderItemId, serialIds });
                }
            } else {
                const qty = qtyByItemId[line.orderItemId] || 0;

                if (qty > 0) {
                    lines.push({ orderItemId: line.orderItemId, qty });
                }
            }
        }

        return lines;
    }, [releasableState, qtyByItemId, selectedSerialsByItemId]);

    const finalizeRelease = () => {
        if (!releaseLines.length) {
            notify(NotificationLevelEnum.warning, "nothing_selected_to_release");
            return;
        }

        setIsFinalizeActive(true);

        const payload: IFinalizeShipmentReleaseDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid,
            lines: releaseLines,
        };

        socket.emit(EndpointsEnum.OrderFinalizeShipmentRelease, payload);
    };

    const lineByItemId = useMemo(() => {
        const map: { [orderItemId: number]: IReleasableLine } = {};

        for (const line of releasableState?.lines || []) {
            map[line.orderItemId] = line;
        }

        return map;
    }, [releasableState]);

    const isShipped = releasableState?.orderStateId === OrderStatesEnum.SHIPPED;
    const hasOutstanding = (releasableState?.lines || []).some((line) => line.outstanding > 0);

    const renderLineRow = (line: IReleasableLine) => {
        const selectedSerials = selectedSerialsByItemId[line.orderItemId] || [];
        const eligibleSerials = line.eligibleSerials || [];
        const ineligibleSerials = line.ineligibleSerials || [];

        return (
            <tr key={line.orderItemId}>
                <td>
                    <div>{line.name}</div>
                    <div className={"text-muted small"}>{line.sku}</div>
                </td>
                <td className={"text-end"}>{line.ordered}</td>
                <td className={"text-end"}>{line.shipped}</td>
                <td className={"text-end"}>{line.outstanding}</td>
                <td className={"release-qty-col"}>
                    {line.outstanding <= 0 ? (
                        <OwLabel title={"shipped"} className={"text-success"} />
                    ) : line.hasSerials ? (
                        <div>
                            <div className={"d-flex align-items-center mb-1"}>
                                <input
                                    id={`selectAllSerials_${line.orderItemId}`}
                                    type={"checkbox"}
                                    className={"form-check-input me-2"}
                                    checked={eligibleSerials.length > 0 && selectedSerials.length === eligibleSerials.length}
                                    onChange={() => toggleAllSerials(line)}
                                    disabled={!eligibleSerials.length}
                                />
                                <label htmlFor={`selectAllSerials_${line.orderItemId}`}>
                                    <OwLabel title={"select_all"} noBottomMargin={true} />
                                    <span className={"ms-1 text-muted small"}>
                                        ({selectedSerials.length}/{eligibleSerials.length})
                                    </span>
                                </label>
                            </div>
                            <div className={"serials-box rounded p-1"}>
                                {eligibleSerials.length || ineligibleSerials.length ? (
                                    <>
                                        {eligibleSerials.map((serial) => (
                                            <div key={serial.id} className={"form-check"}>
                                                <input
                                                    id={`serial_${serial.id}`}
                                                    type={"checkbox"}
                                                    className={"form-check-input"}
                                                    checked={selectedSerials.includes(serial.id)}
                                                    onChange={() => toggleSerial(line, serial.id)}
                                                />
                                                <label className={"form-check-label"} htmlFor={`serial_${serial.id}`}>
                                                    {serial.value || serial.id}
                                                </label>
                                            </div>
                                        ))}
                                        {ineligibleSerials.map((serial) => (
                                            <div key={serial.id} className={"form-check"}>
                                                <input
                                                    id={`serial_${serial.id}`}
                                                    type={"checkbox"}
                                                    className={"form-check-input"}
                                                    checked={false}
                                                    disabled={true}
                                                    readOnly={true}
                                                />
                                                <label className={"form-check-label text-muted"} htmlFor={`serial_${serial.id}`}>
                                                    {serial.value || serial.id}
                                                    <InProductionBadge stepName={serial.stepName} />
                                                </label>
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    <OwLabel title={"no_eligible_serials"} className={"text-muted"} noBottomMargin={true} />
                                )}
                            </div>
                        </div>
                    ) : (
                        <OwInputWrapper
                            name={`releaseQty_${line.orderItemId}`}
                            type={"number"}
                            min={0}
                            max={line.outstanding}
                            value={qtyByItemId[line.orderItemId] ?? 0}
                            fn={(e: any) => handleQtyChange(line, e.target.value)}
                        />
                    )}
                </td>
            </tr>
        );
    };

    const renderConfirmation = () => {
        return (
            <div className={"mt-3"}>
                <OwLabel isBold={true} title={"confirm_release_summary"} />
                <table className={"table table-sm w-auto"}>
                    <thead>
                        <tr>
                            <th>
                                <OwLabel title={"name"} noBottomMargin={true} />
                            </th>
                            <th className={"text-end"}>
                                <OwLabel title={"release_qty"} noBottomMargin={true} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {releaseLines.map((releaseLine) => {
                            const line = lineByItemId[releaseLine.orderItemId];
                            const qty = releaseLine.serialIds ? releaseLine.serialIds.length : releaseLine.qty || 0;

                            return (
                                <tr key={releaseLine.orderItemId}>
                                    <td>
                                        {line?.name} <span className={"text-muted small"}>{line?.sku}</span>
                                        {releaseLine.serialIds ? (
                                            <div className={"text-muted small"}>
                                                {(line?.eligibleSerials || [])
                                                    .filter((serial) => releaseLine.serialIds!.includes(serial.id))
                                                    .map((serial) => serial.value || serial.id)
                                                    .join(", ")}
                                            </div>
                                        ) : null}
                                    </td>
                                    <td className={"text-end"}>{qty}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <div className={"d-flex"}>
                    <OwButton
                        title={"back"}
                        leftIconClass={"fas fa-arrow-left"}
                        fn={() => setIsConfirming(false)}
                        disabled={isFinalizeActive}
                    />
                    <div className={"ms-2"}>
                        <OwButton
                            className={"btn-success"}
                            title={"confirm_release"}
                            leftIconClass={"fad fa-truck-loading"}
                            fn={finalizeRelease}
                            active={isFinalizeActive}
                            disableOnActive={true}
                        />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={"ShipmentReleaseView p-3"}>
            <Breadcrumbs crumbs={crumbs} />

            <div className={"d-flex align-items-center mt-2 mb-3"}>
                <h4 className={"mb-0"}>
                    <OwLabel title={"release_for_shipment"} noBottomMargin={true} />
                </h4>
                {releasableState?.partiallyShipped ? <OwBadge title={"partially_shipped"} bgColor={"bg-warning"} /> : null}
                {isShipped ? <OwBadge title={"shipped"} bgColor={"bg-success"} /> : null}
            </div>

            {releasableState && !releasableState.releaseAllowed ? (
                <OwLabel className={"text-danger"} title={"release_not_allowed_at_step"} />
            ) : null}

            {releasableState ? (
                <>
                    <table className={"table"}>
                        <thead>
                            <tr>
                                <th>
                                    <OwLabel title={"name"} noBottomMargin={true} />
                                </th>
                                <th className={"text-end"}>
                                    <OwLabel title={"ordered"} noBottomMargin={true} />
                                </th>
                                <th className={"text-end"}>
                                    <OwLabel title={"shipped"} noBottomMargin={true} />
                                </th>
                                <th className={"text-end"}>
                                    <OwLabel title={"outstanding"} noBottomMargin={true} />
                                </th>
                                <th>
                                    <OwLabel title={"release_qty"} noBottomMargin={true} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>{releasableState.lines.map(renderLineRow)}</tbody>
                    </table>

                    {isConfirming ? (
                        renderConfirmation()
                    ) : (
                        <div className={"d-flex"}>
                            <OwButton
                                title={"back"}
                                leftIconClass={"fas fa-arrow-left"}
                                fn={() => navigate(`/orders/${orderGuid}/entry`)}
                            />
                            {releasableState.releaseAllowed && hasOutstanding ? (
                                <div className={"ms-2"}>
                                    <OwButton
                                        className={"btn-success"}
                                        title={"release_for_shipment"}
                                        leftIconClass={"fad fa-truck-loading"}
                                        fn={() => {
                                            if (!releaseLines.length) {
                                                notify(NotificationLevelEnum.warning, "nothing_selected_to_release");
                                                return;
                                            }
                                            setIsConfirming(true);
                                        }}
                                    />
                                </div>
                            ) : null}
                        </div>
                    )}
                </>
            ) : null}
        </div>
    );
};

export default ShipmentReleaseView;
